import { EyeOff } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  hiddenCounts,
  hiddenRegionsOf,
  isHiddenByMe,
  NOTHING_HIDDEN,
  setChannelHidden,
  setRegionsHidden,
} from "./hideMine";
import type { HiddenByMe } from "./hideMine";

/** Канал для пикера: код, подпись, класс бейджа и его регионы. Собирает страница — здесь незачем
 *  вторая копия списка каналов и их цветов. */
export interface HideChannel {
  code: string;
  label: string;
  badge: string;
  regions: readonly string[];
}

/** «Скрыть у себя» — ЛИЧНЫЙ фильтр обзора (только этот человек, только этот браузер).
 *  Стоит рядом с «Куда планируем» намеренно: выбор между «убрать с экрана» и «не планировать
 *  туда» человек делает в одном месте — первое числа не трогает, второе меняет расчёт.
 *  `memo` — как у `PlanningTargets`: родитель перерисовывается на каждый кадр скролла, а внутри
 *  под сотню чекбоксов. */
export const DistributionHidePicker = React.memo(function DistributionHidePicker({
  channels,
  hidden,
  onChange,
}: {
  channels: readonly HideChannel[];
  hidden: HiddenByMe;
  onChange: (next: HiddenByMe) => void;
}): React.ReactElement {
  const counts = hiddenCounts(hidden);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          title="Скрыть строки у себя: личный фильтр обзора. Потребность и движок не меняет, но фиксация увозит только показанное"
        >
          <EyeOff className="size-4" />
          Скрыть у себя
          {counts.total > 0 && (
            <span className="ml-1 rounded bg-slate-200 px-1 text-[10px] font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              скрыто {counts.total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[70vh] w-96 overflow-auto p-3 text-sm"
      >
        <p className="mb-2 rounded border border-slate-300 bg-slate-50 px-2 py-1.5 text-xs dark:border-slate-700 dark:bg-slate-900">
          <b>Только у вас, в этом браузере.</b> Снятые строки исчезают из таблицы,
          из итогов и из действий над выделением. Потребность, «Решения» и движок
          не меняются — скрытое они считают как раньше. Но <b>фиксация увозит
          ровно показанное</b>: решения по скрытым строкам останутся сохранёнными
          и в поставки не уедут (кнопка и подтверждение говорят об этом прямо).
          Чтобы куда-то <b>не возить</b> — это «Куда планируем»: там выключенное
          пропадает из вашей потребности и фиксации.
        </p>
        {counts.total > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="mb-2 h-7 w-full"
            onClick={() => onChange(NOTHING_HIDDEN)}
          >
            Показать всё ({counts.total})
          </Button>
        )}
        {channels.map((c) => {
          // регионы из отчётов ∪ уже скрытые: выпавший из отчётов регион иначе нечем вернуть
          const list = [
            ...new Set([...c.regions, ...hiddenRegionsOf(hidden, c.code)]),
          ].sort((a, b) => a.localeCompare(b, "ru"));
          const chHidden = isHiddenByMe(hidden, c.code);
          const hiddenCount = list.filter((r) =>
            isHiddenByMe(hidden, c.code, r),
          ).length;
          return (
            <div key={c.code} className="mb-3 last:mb-0">
              <label className="flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={!chHidden}
                  onChange={(e) =>
                    onChange(setChannelHidden(hidden, c.code, !e.target.checked))
                  }
                />
                <span className={cn("rounded px-1.5 py-0.5 text-xs", c.badge)}>
                  {c.label}
                </span>
                {chHidden && (
                  <span className="text-xs text-muted-foreground">
                    скрыт у вас целиком
                  </span>
                )}
              </label>
              <div className={cn("mt-1 grid gap-0.5 pl-6", chHidden && "opacity-40")}>
                <label className="flex items-center gap-2 border-b pb-1 text-xs font-medium">
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el)
                        el.indeterminate =
                          hiddenCount > 0 && hiddenCount < list.length;
                    }}
                    checked={hiddenCount === 0}
                    disabled={chHidden || list.length === 0}
                    onChange={() =>
                      onChange(
                        setRegionsHidden(hidden, c.code, list, hiddenCount === 0),
                      )
                    }
                  />
                  все регионы
                  {hiddenCount > 0 && (
                    <span className="font-normal text-muted-foreground">
                      · скрыто {hiddenCount}
                    </span>
                  )}
                </label>
                {list.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={!isHiddenByMe(hidden, c.code, r)}
                      disabled={chHidden}
                      onChange={(e) =>
                        onChange(
                          setRegionsHidden(
                            hidden,
                            c.code,
                            [r],
                            !e.target.checked,
                          ),
                        )
                      }
                    />
                    {r}
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
});
